/**
 * HR Comparison Page
 *
 * Side-by-side comparison of multiple evaluations for HR analysis.
 */

import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useEvaluations, useEvaluation, useCalculateScores } from '../evaluations/useEvaluations';
import { ScoreDisplay } from '../evaluations/wizard/ScoreInput';
import type { EvaluationStatus } from '../../lib/evaluationsApi';

const STATUS_LABELS: Record<EvaluationStatus, string> = {
  draft: 'Concept',
  submitted: 'Ingediend',
  in_review: 'In Review',
  final: 'Definitief',
  cancelled: 'Geannuleerd',
};

function EvaluationSelector({
  selectedIds,
  onSelect,
}: {
  selectedIds: number[];
  onSelect: (ids: number[]) => void;
}) {
  const { data: evaluations, isLoading } = useEvaluations();

  if (isLoading) return <div className="text-gray-500">Laden...</div>;

  const availableEvaluations = evaluations?.data?.filter(
    (e) => e.status === 'final' || e.status === 'in_review'
  ) || [];

  const handleToggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onSelect(selectedIds.filter((i) => i !== id));
    } else if (selectedIds.length < 4) {
      onSelect([...selectedIds, id]);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-3">
        Selecteer beoordelingen om te vergelijken (max. 4)
      </h3>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {availableEvaluations.map((evaluation) => (
          <label
            key={evaluation.id}
            className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
              selectedIds.includes(evaluation.id) ? 'bg-blue-50' : ''
            }`}
          >
            <input
              type="checkbox"
              checked={selectedIds.includes(evaluation.id)}
              onChange={() => handleToggle(evaluation.id)}
              disabled={!selectedIds.includes(evaluation.id) && selectedIds.length >= 4}
              className="rounded border-gray-300"
            />
            <div className="flex-1">
              <span className="text-sm font-medium">
                Medewerker #{evaluation.employeeId}
              </span>
              <span className="text-xs text-gray-500 ml-2">
                ({new Date(evaluation.periodeStart).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })})
              </span>
            </div>
            <span className="text-sm text-gray-600">
              {evaluation.totalScore || '—'}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function ComparisonColumn({ evaluationId }: { evaluationId: number }) {
  const { data: evaluation, isLoading: evalLoading } = useEvaluation(evaluationId);
  const { data: calculation, isLoading: calcLoading } = useCalculateScores(evaluationId);

  const isLoading = evalLoading || calcLoading;

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <div className="bg-red-50 rounded-lg border border-red-200 p-4">
        <p className="text-red-700 text-sm">Beoordeling niet gevonden</p>
      </div>
    );
  }

  // Use calculation breakdown for names, fallback to raw scores
  const tasks = calculation?.breakdown?.tasks || [];
  const vakCompetencies = calculation?.breakdown?.vakCompetencies || [];
  const basisCompetencies = calculation?.breakdown?.basisCompetencies || [];
  const selfEvaluations = evaluation.selfEvaluations || [];

  // Create a map of taskId -> name for self-evaluation display
  const taskNameMap = new Map(tasks.map((t) => [t.taskId, t.name]));

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="font-medium text-gray-900">
          {evaluation.employee?.name || `Medewerker #${evaluation.employeeId}`}
        </h3>
        <p className="text-xs text-gray-500">
          {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL')} -{' '}
          {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL')}
        </p>
        <span className={`mt-1 inline-block px-2 py-0.5 text-xs rounded-full ${
          evaluation.status === 'final' ? 'bg-green-100 text-green-800' :
          evaluation.status === 'in_review' ? 'bg-purple-100 text-purple-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {STATUS_LABELS[evaluation.status]}
        </span>
      </div>

      {/* Scores Summary */}
      <div className="p-4 border-b border-gray-200">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-xs text-gray-500">Taken</p>
            <p className="text-lg font-bold text-gray-900">
              {calculation?.taskScore?.toFixed(1) || evaluation.taskScore || '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Comp.</p>
            <p className="text-lg font-bold text-gray-900">
              {calculation ? ((calculation.vakCompetencyScore || 0) + (calculation.basisCompetencyScore || 0)).toFixed(1) : evaluation.competencyScore || '—'}
            </p>
          </div>
          <div className="bg-green-50 rounded p-1">
            <p className="text-xs text-green-700">Totaal</p>
            <p className="text-lg font-bold text-green-900">
              {calculation?.totalScore?.toFixed(1) || evaluation.totalScore || '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Task Scores */}
      <div className="p-4 border-b border-gray-200">
        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Taakscores</h4>
        {tasks.length > 0 ? (
          <div className="space-y-1">
            {tasks.map((task) => (
              <div key={task.taskId} className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-600 truncate flex-1" title={task.name}>
                  {task.name}
                </span>
                <ScoreDisplay score={task.score} size="sm" showLabel={false} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">Geen taakscores</p>
        )}
      </div>

      {/* Self-evaluation comparison */}
      {selfEvaluations.length > 0 && tasks.length > 0 && (
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-xs font-medium text-yellow-700 uppercase mb-2">
            Zelf-evaluatie verschil
          </h4>
          <div className="space-y-1">
            {selfEvaluations.map((se) => {
              const task = tasks.find((t) => t.taskId === se.taskId);
              const diff = task ? task.score - se.score : null;
              const taskName = taskNameMap.get(se.taskId) || `Taak #${se.taskId}`;
              return (
                <div key={se.taskId} className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-600 truncate flex-1" title={taskName}>
                    {taskName}
                  </span>
                  {diff !== null ? (
                    <span className={`text-xs font-medium ${
                      Math.abs(diff) >= 2 ? 'text-red-600' :
                      diff > 0 ? 'text-green-600' :
                      diff < 0 ? 'text-orange-600' : 'text-gray-500'
                    }`}>
                      {diff > 0 ? '+' : ''}{diff}
                    </span>
                  ) : '—'}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vak Competencies */}
      {vakCompetencies.length > 0 && (
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Vakcompetenties</h4>
          <div className="space-y-1">
            {vakCompetencies.map((comp) => (
              <div key={comp.id} className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-600 truncate flex-1" title={comp.name}>
                  {comp.name}
                </span>
                <ScoreDisplay score={comp.score} size="sm" showLabel={false} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Basis Competencies */}
      <div className="p-4">
        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Basiscompetenties</h4>
        {basisCompetencies.length > 0 ? (
          <div className="space-y-1">
            {basisCompetencies.map((comp) => (
              <div key={comp.id} className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-600 truncate flex-1" title={comp.name}>
                  {comp.name}
                </span>
                <ScoreDisplay score={comp.score} size="sm" showLabel={false} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">Geen basiscompetenties</p>
        )}
      </div>
    </div>
  );
}

export function HRComparisonPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get selected IDs from URL
  const selectedIds = useMemo(() => {
    const ids = searchParams.get('ids');
    if (!ids) return [];
    return ids.split(',').map(Number).filter((n) => !isNaN(n));
  }, [searchParams]);

  const handleSelectIds = (ids: number[]) => {
    if (ids.length > 0) {
      setSearchParams({ ids: ids.join(',') });
    } else {
      setSearchParams({});
    }
  };

  return (
    <Layout>
      <div data-testid="hr-comparison-page" className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/hr')}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Terug naar overzicht
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Beoordelingen Vergelijken</h1>
          <p className="mt-1 text-sm text-gray-500">
            Vergelijk tot 4 beoordelingen naast elkaar voor analyse en consistentiecontrole.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Selector */}
          <div className="lg:col-span-1">
            <EvaluationSelector selectedIds={selectedIds} onSelect={handleSelectIds} />
          </div>

          {/* Comparison Grid */}
          <div className="lg:col-span-4">
            {selectedIds.length === 0 ? (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-12 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                <h3 className="mt-4 text-sm font-medium text-gray-900">Geen beoordelingen geselecteerd</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Selecteer 2-4 beoordelingen in het linkerpaneel om ze te vergelijken.
                </p>
              </div>
            ) : (
              <div className={`grid gap-4 ${
                selectedIds.length === 1 ? 'grid-cols-1' :
                selectedIds.length === 2 ? 'grid-cols-2' :
                selectedIds.length === 3 ? 'grid-cols-3' :
                'grid-cols-2 lg:grid-cols-4'
              }`}>
                {selectedIds.map((id) => (
                  <ComparisonColumn key={id} evaluationId={id} />
                ))}
              </div>
            )}

            {/* Analysis Summary */}
            {selectedIds.length >= 2 && (
              <div className="mt-6 bg-blue-50 rounded-lg border border-blue-200 p-4">
                <h3 className="text-sm font-medium text-blue-900 mb-2">Analyse Tips</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Let op grote verschillen tussen zelf-evaluatie en manager-score (≥2)</li>
                  <li>• Vergelijk competentiescores voor consistentie tussen beoordelaars</li>
                  <li>• Check of vergelijkbare functies vergelijkbare scores krijgen</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
